import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings, Save, HeartOff } from "lucide-react";

export default function CitizenPreferences() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [interests, setInterests] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", language: "ca", interests: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/citizens/preferences/${token}`),
      api.get("/interests?active_only=true"),
    ]).then(([p, i]) => {
      setData(p.data);
      setInterests(i.data);
      setForm({ name: p.data.name, phone: p.data.phone || "", language: p.data.language, interests: p.data.interests });
    }).catch(() => setData({ error: true }));
  }, [token]);

  const toggleInterest = (slug) => setForm(f => ({ ...f, interests: f.interests.includes(slug) ? f.interests.filter(s => s !== slug) : [...f.interests, slug] }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/citizens/preferences/${token}`, form);
      toast.success("Preferències actualitzades");
    } catch { toast.error("Error"); }
    setSaving(false);
  };

  if (!data) return <PublicLayout><div className="py-20 text-center text-muted-foreground">Carregant…</div></PublicLayout>;
  if (data.error) return <PublicLayout><div className="py-20 text-center text-muted-foreground">Enllaç invàlid.</div></PublicLayout>;

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-6 py-16">
        <Card className="rounded-3xl border-2 shadow-xl">
          <CardContent className="p-8 space-y-6" data-testid="citizen-prefs">
            <div>
              <div className="w-12 h-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center mb-3">
                <Settings className="w-5 h-5" aria-hidden="true" />
              </div>
              <div className="overline text-accent">Preferències</div>
              <h1 className="font-heading font-bold text-2xl mt-1">Les meves subscripcions</h1>
              <p className="text-sm text-muted-foreground mt-1">Compte: <strong>{data.email}</strong></p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-xl" data-testid="prefs-name" /></div>
              <div><Label>Telèfon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 rounded-xl" data-testid="prefs-phone" /></div>
              <div className="sm:col-span-2"><Label>Idioma preferit</Label>
                <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="mt-2 w-full rounded-xl border-2 border-border h-10 px-3 bg-background" data-testid="prefs-language">
                  <option value="ca">Català</option><option value="es">Español</option><option value="en">English</option><option value="fr">Français</option>
                </select>
              </div>
            </div>

            <div>
              <Label>Els meus interessos</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                {interests.map(i => (
                  <button key={i.slug} type="button" onClick={() => toggleInterest(i.slug)} data-testid={`prefs-interest-${i.slug}`}
                    className={`text-left px-3 py-2 rounded-xl border-2 text-sm transition-colors ${
                      form.interests.includes(i.slug) ? "border-accent bg-accent/10 text-primary" : "border-border"
                    }`}>{i.name_ca}</button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button onClick={submit} disabled={saving} className="rounded-full" data-testid="prefs-save-btn">
                <Save className="w-4 h-4 mr-2" aria-hidden="true" /> {saving ? "Desant…" : "Desar preferències"}
              </Button>
              <Button asChild variant="outline" className="rounded-full" data-testid="prefs-unsub-link">
                <Link to={`/baixa/${token}`}><HeartOff className="w-4 h-4 mr-2" aria-hidden="true" /> Donar-me de baixa</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}
